import { db } from '../instant';
import { id } from '@instantdb/react';
import { ItemType } from '../schema';
import { uploadImage, getImageUrl, cacheImage, deleteCachedImage } from '../upload';

interface CreateItemTypeParams {
    name: string;
    description?: string;
    categoryId: string;
    photoUrl?: string;
    consumable: boolean;
    photo?: File | null;
    token?: string;
}

/**
 * Create a new item type with optional photo upload
 */
export async function createItemType(params: CreateItemTypeParams, existingItemType?: ItemType): Promise<string> {
    const itemTypeId = existingItemType?.id || id();
    const txs: any[] = [];

    let finalPhotoUrl = params.photoUrl;

    // Handle photo upload if a new photo file is provided
    if (params.photo) {
        // New or updated photo
        if (!params.token) {
            throw new Error('User token is required to upload images');
        }
        const token = params.token;

        const imgUrl = await getImageUrl(params.photo);

        // Delete old cached photo if updating with a new one
        if (existingItemType?.photoUrl && imgUrl !== existingItemType.photoUrl) {
            try {
                await deleteCachedImage(existingItemType.photoUrl);
            } catch (error) {
                console.warn('Failed to delete old cached item photo:', error);
                // Continue with upload even if cache cleanup fails
            }
        }

        await cacheImage(params.photo, imgUrl, token);
        await uploadImage(params.photo, imgUrl, token);
        finalPhotoUrl = imgUrl;
    } else if (existingItemType && params.photoUrl === null) {
        // Photo was explicitly removed - clear from cache
        if (existingItemType.photoUrl) {
            try {
                await deleteCachedImage(existingItemType.photoUrl);
            } catch (error) {
                console.warn('Failed to delete cached item photo:', error);
            }
        }
        finalPhotoUrl = undefined;
    } else if (existingItemType && !params.photo) {
        // Keep existing photo
        finalPhotoUrl = existingItemType.photoUrl || params.photoUrl;
    }

    // Get max order for new items
    const maxOrder = existingItemType
        ? existingItemType.order || 0
        : (await db.queryOnce({ itemTypes: {} })).data?.itemTypes?.reduce(
              (max: number, item: { order?: number }) => Math.max(max, item.order || 0),
              0
          ) || 0;

    // Create or update item type
    txs.push(
        db.tx.itemTypes[itemTypeId].update({
            name: params.name,
            description: params.description,
            photoUrl: finalPhotoUrl,
            consumable: params.consumable,
            order: existingItemType ? existingItemType.order : maxOrder + 1,
        })
    );

    // Link to category (when editing, unlink previous category first — link() only adds)
    const previousCategoryId = existingItemType?.category?.id;
    if (previousCategoryId && previousCategoryId !== params.categoryId) {
        txs.push(db.tx.itemTypes[itemTypeId].unlink({ category: previousCategoryId }));
    }
    txs.push(db.tx.itemTypes[itemTypeId].link({ category: params.categoryId }));

    // Create initial itemLocation at first storage location (e.g. HQ) when creating new item type
    if (!existingItemType) {
        const { data: storageData } = await db.queryOnce({ storageLocations: { $: { order: { order: 'asc' } } } });
        const firstStorage = storageData?.storageLocations?.[0];
        const locationId = id();
        txs.push(
            db.tx.itemLocations[locationId].update({
                quantity: 0,
            })
        );
        txs.push(db.tx.itemLocations[locationId].link({ itemType: itemTypeId }));
        if (firstStorage?.id) {
            txs.push(db.tx.itemLocations[locationId].link({ storageLocation: firstStorage.id }));
        }
    }

    await db.transact(txs);
    return itemTypeId;
}

/**
 * Update an existing item type
 */
export async function updateItemType(
    itemTypeId: string,
    updates: {
        name?: string;
        description?: string;
        categoryId?: string;
        /** When changing category, pass the previous category id so we can unlink it. */
        previousCategoryId?: string;
        photoUrl?: string;
        consumable?: boolean;
    }
): Promise<void> {
    const txs: any[] = [];

    const updateFields: any = {};
    if (updates.name !== undefined) updateFields.name = updates.name;
    if (updates.description !== undefined) updateFields.description = updates.description;
    if (updates.photoUrl !== undefined) updateFields.photoUrl = updates.photoUrl;
    if (updates.consumable !== undefined) updateFields.consumable = updates.consumable;

    if (Object.keys(updateFields).length > 0) {
        txs.push(db.tx.itemTypes[itemTypeId].update(updateFields));
    }

    if (updates.categoryId) {
        if (updates.previousCategoryId && updates.previousCategoryId !== updates.categoryId) {
            txs.push(db.tx.itemTypes[itemTypeId].unlink({ category: updates.previousCategoryId }));
        }
        txs.push(db.tx.itemTypes[itemTypeId].link({ category: updates.categoryId }));
    }

    if (txs.length > 0) {
        await db.transact(txs);
    }
}

/**
 * Delete an item type
 * Throws error if item has inventory (quantity > 0)
 */
export async function deleteItemType(itemType: ItemType): Promise<void> {
    // Query item locations for this item type
    const { data } = await db.queryOnce({
        itemLocations: {
            $: {
                where: {
                    'itemType.id': itemType.id,
                },
            },
        },
    });

    const locations = data?.itemLocations || [];
    const totalQuantity = locations.reduce((sum: number, loc: { quantity?: number }) => sum + (loc.quantity || 0), 0);

    if (totalQuantity > 0) {
        throw new Error('Cannot delete item with inventory');
    }

    // Delete cached photo to free up cache space
    if (itemType.photoUrl) {
        try {
            await deleteCachedImage(itemType.photoUrl);
        } catch (error) {
            console.warn('Failed to delete cached item photo:', error);
            // Continue with deletion even if cache cleanup fails
        }
    }

    const txs: any[] = [];

    // Delete all itemLocations (should all be quantity 0)
    for (const location of locations) {
        txs.push(db.tx.itemLocations[location.id].delete());
    }

    // Delete itemType
    txs.push(db.tx.itemTypes[itemType.id].delete());

    await db.transact(txs);
}
